export type Theme = "light" | "dark" | "auto";
let cachedSystemTheme: "light" | "dark" | null = null;

export const getSystemTheme = (): "light" | "dark" => {
  if (cachedSystemTheme !== null) {
    return cachedSystemTheme;
  }

  if (typeof window === "undefined") {
    cachedSystemTheme = "light";
    return "light";
  }

  if (!window.matchMedia) {
    cachedSystemTheme = "light";
    return "light";
  }

  try {
    const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const isDark = darkModeQuery.matches;
    cachedSystemTheme = isDark ? "dark" : "light";
    return cachedSystemTheme;
  } catch (error) {
    console.warn("Error detecting system theme:", error);
    cachedSystemTheme = "light";
    return "light";
  }
};

export const getInitialTheme = (): Theme => {
  if (typeof window === "undefined") {
    return "auto";
  }

  try {
    const stored = localStorage.getItem("theme");
    if (stored === "light" || stored === "dark" || stored === "auto") {
      return stored;
    }
  } catch (error) {
    console.warn("Error reading theme from localStorage:", error);
  }

  return "auto";
};

export const getEffectiveTheme = (theme: Theme): "light" | "dark" => {
  if (theme === "auto") {
    return getSystemTheme();
  }
  return theme;
};

export const applyTheme = (theme: Theme): void => {
  if (typeof document === "undefined") {
    return;
  }

  try {
    const effectiveTheme = getEffectiveTheme(theme);
    document.documentElement.setAttribute("data-theme", effectiveTheme);
    // The browser bar (and an installed app's status bar) takes the page
    // colour, --ui-page-bg-default; public/index.html sets it before paint.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute(
        "content",
        effectiveTheme === "dark" ? "#0a0a0a" : "#f5f6fc",
      );

    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("themeChanged"));
    }
  } catch (error) {
    console.warn("Error applying theme:", error);
  }
};

export const setupThemeChangeListener = (
  callback: () => void,
): (() => void) => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => {};
  }

  try {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      cachedSystemTheme = null;
      callback();
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  } catch (error) {
    console.warn("Error setting up theme change listener:", error);
  }

  return () => {};
};

export const supportsAutoTheme = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return (
      !!window.matchMedia &&
      typeof window.matchMedia("(prefers-color-scheme: dark)").matches ===
        "boolean"
    );
  } catch (error) {
    return false;
  }
};
